import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { isEmpty, isNil } from 'lodash';
import { AutomatedTransferNotificationMessage } from '../../../../../../pages/transfers/automated-transfers/contract/automated-transfers-notification.contract';
import { AutomatedTransferNotification } from '../../../../../../pages/transfers/contract/automated-transfer-notification.contract';
import { AutomatedTransferNotificationType } from '../../../../../../pages/transfers/enum/automated-transfer-notification-type.enum';
import { DatesService } from '../../../../../../shared/services/dates.service';
import { LanguageService } from '../../../../../services/language.service';

@Injectable({ providedIn: 'root' })
export class ATNotificationToMessageService {
  private languageService = inject(LanguageService);
  private router = inject(Router);
  private datesService = inject(DatesService);

  convert(notifications: AutomatedTransferNotification[]): (AutomatedTransferNotificationMessage | null)[] {
    return notifications.map((notification: AutomatedTransferNotification) => {
      const message = this.convertToMessageWithoutRedirectCallback(notification);
      return {
        ...message,
        redirectCb: this.getRedirectCallbackForType(notification.notificationType, notification.redirectUrl),
      };
    });
  }

  private getRedirectCallbackForType(notificationType: AutomatedTransferNotificationType, redirectUrl?: string) {
    switch (notificationType) {
      case AutomatedTransferNotificationType.AUTOMATED_TRANSFER_COMPLETED: // behaviour defined -> do nothing
      case AutomatedTransferNotificationType.AUTOMATED_TRANSFER_CANCELLED:
      case AutomatedTransferNotificationType.AUTOMATED_TRANSFER_DELIVERY_TIME_EXCEEDED:
      case AutomatedTransferNotificationType.AUTOMATED_TRANSFER_GEOFENCE_CHANGES:
        return () => {
          // behaviour not defined
        };

      default:
        return () => {
          if (redirectUrl) {
            this.router.navigateByUrl(redirectUrl);
          }
        };
    }
  }

  private convertToMessageWithoutRedirectCallback({
    notificationId,
    notificationType,
    notificationBodyTranslationKey,
    notificationTitleTranslationKey,
    notificationTranslationParams,
  }: AutomatedTransferNotification): AutomatedTransferNotificationMessage {
    const formattedParams = this.searchAndMakeDateParamsReadable(notificationTranslationParams);

    return {
      notificationId,
      notificationType,
      title: this.translate(notificationTitleTranslationKey),
      body: this.translate(notificationBodyTranslationKey, formattedParams),
      redirectCb: () => {},
    };
  }

  private searchAndMakeDateParamsReadable(notificationTranslationParams: { [key: string]: string }) {
    if (isNil(notificationTranslationParams) || isEmpty(notificationTranslationParams)) {
      return;
    }

    const paramsKeys = Object.keys(notificationTranslationParams || {});
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const readableParams = paramsKeys.reduce((acc: any, key: string) => {
      const val = notificationTranslationParams[key];
      const isDate = typeof val === 'string' && DatesService.isISOFormatDate(val);

      acc[key] = isDate ? this.formatTimestamp(val) : val;
      return acc;
    }, {});

    return readableParams;
  }

  private formatTimestamp(timestamp: string) {
    return this.datesService.dateFormatLocale(timestamp, {
      dateFormat: 'LL',
      showTime: true,
      hideEndOfDayTime: false,
    });
  }

  private translate(key: string, interpolateParams?: object): string {
    return this.languageService.getInstant(key, interpolateParams);
  }
}
